import React from "react";
import {
  Box,
  Typography,
  Grid,
  Zoom,
  Slide,
  Fade,
  Grow,
  CardContent,
  Card,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronRight } from "@mui/icons-material";
import { TrendingUp, Zap, Plus, Search, BellIcon, CheckCircle2, Layers, KeyRound, Server, Camera as CameraIconL } from "lucide-react";
import { StyledCard, MetricValue, MetricLabel } from "../../theme/theme";
import { getSystemDetails } from "../../utils/formatting";
import { getRegistrationTrend } from "../../utils/licenseStatus";
import { Sparkline } from "../../shared/components/Sparkline";

function DashboardHome({
  darkMode,
  theme,
  currentUser,
  licenses,
  systems,
  cameras,
  projects,
  displayedLicenses,
  displayedSystems,
  displayedCameras,
  displayedProjects,
  activeLicenses,
  expiredLicenses,
  systemsWithAMC,
  systemsWithoutAMC,
  systemUtilization,
  cameraOnlineRatio,
  onlineCameras,
  offlineCameras,
  totalCameras,
  searchTerm,
  handleSearch,
  handleOpenDialog,
  handleAddSystem,
  handleProjectSelect,
  setActiveView,
  isMobile,
}) {
  const licenseTrend = getRegistrationTrend(licenses);
  const systemTrend = getRegistrationTrend(systems);
  const cameraTrend = getRegistrationTrend(cameras);
  const projectTrend = getRegistrationTrend(systems.filter(s => s.project_id));

  return (
    <Fade in={true} timeout={500}>
      <Box key="dashboard-view">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
            Welcome back, {currentUser}!
          </Typography>
          <Typography variant="body1" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
            Here's what's happening with your license management system today.
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2 }}>
                      <KeyRound color={theme.palette.primary.main} size={22} />
                    </Box>
                    <Chip
                      label={`${((activeLicenses / (licenses.length || 1)) * 100).toFixed(0)}% Active`}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 600 }}
                    />
                  </Box>
                  <MetricValue>{displayedLicenses}</MetricValue>
                  <MetricLabel sx={{ mb: 2 }}>Total Licenses</MetricLabel>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Active
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                        {activeLicenses}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Expired
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                        {expiredLicenses}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, borderRadius: 4, height: 6, bgcolor: darkMode ? '#16213A' : '#EAF0F4', overflow: 'hidden', display: 'flex' }}>
                    <Box sx={{ width: `${licenses.length > 0 ? (activeLicenses / licenses.length * 100) : 0}%`, bgcolor: 'success.main', height: '100%', transition: 'width 0.8s ease' }} />
                    <Box sx={{ flex: 1, bgcolor: 'error.main', height: '100%' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Sparkline data={licenseTrend} color={theme.palette.primary.main} />
                  </Box>
                </CardContent>
              </StyledCard>
            </Zoom>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '200ms' }}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.08), borderRadius: 2 }}>
                      <Server color={theme.palette.secondary.main} size={22} />
                    </Box>
                    <Chip
                      label={`${systemUtilization}% AMC`}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 600 }}
                    />
                  </Box>
                  <MetricValue>{displayedSystems}</MetricValue>
                  <MetricLabel sx={{ mb: 2 }}>Total Systems</MetricLabel>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        With AMC
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                        {systemsWithAMC}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Without AMC
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        {systemsWithoutAMC}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, borderRadius: 4, height: 6, bgcolor: darkMode ? '#16213A' : '#EAF0F4', overflow: 'hidden', display: 'flex' }}>
                    <Box sx={{ width: `${systems.length > 0 ? (systemsWithAMC / systems.length * 100) : 0}%`, bgcolor: 'info.main', height: '100%', transition: 'width 0.8s ease' }} />
                    <Box sx={{ flex: 1, bgcolor: 'warning.main', height: '100%' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Sparkline data={systemTrend} color={theme.palette.secondary.main} />
                  </Box>
                </CardContent>
              </StyledCard>
            </Zoom>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '300ms' }}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: alpha('#16A987', 0.08), borderRadius: 2 }}>
                      <CameraIconL color="#16A987" size={22} />
                    </Box>
                    <Chip
                      label={`${cameraOnlineRatio}% Online`}
                      size="small"
                      sx={{ bgcolor: alpha('#16A987', 0.1), color: '#16A987', fontWeight: 600 }}
                    />
                  </Box>
                  <MetricValue>{displayedCameras}</MetricValue>
                  <MetricLabel sx={{ mb: 2 }}>Total Cameras</MetricLabel>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Online
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                        {onlineCameras}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Offline
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                        {offlineCameras}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, borderRadius: 4, height: 6, bgcolor: darkMode ? '#16213A' : '#EAF0F4', overflow: 'hidden', display: 'flex' }}>
                    <Box sx={{ width: `${totalCameras > 0 ? (onlineCameras / totalCameras * 100) : 0}%`, bgcolor: 'success.main', height: '100%', transition: 'width 0.8s ease' }} />
                    <Box sx={{ flex: 1, bgcolor: darkMode ? '#16213A' : '#D8E1E8', height: '100%' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Sparkline data={cameraTrend} color="#16A987" />
                  </Box>
                </CardContent>
              </StyledCard>
            </Zoom>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '400ms' }}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: alpha('#9B8CFA', 0.08), borderRadius: 2 }}>
                      <Layers sx={{ color: '#9B8CFA' }} />
                    </Box>
                    <Chip
                      label={`${projects.length} Total`}
                      size="small"
                      sx={{ bgcolor: alpha('#9B8CFA', 0.1), color: '#9B8CFA', fontWeight: 600 }}
                    />
                  </Box>
                  <MetricValue>{displayedProjects}</MetricValue>
                  <MetricLabel sx={{ mb: 2 }}>Active Projects</MetricLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp size={16} color={theme.palette.success.main} />
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                      +{projects.reduce((acc, p) => acc + p.systems.length, 0)} systems
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2, borderRadius: 4, height: 6, bgcolor: darkMode ? '#16213A' : '#EAF0F4', overflow: 'hidden' }}>
                    <Box sx={{ width: projects.length > 0 ? '100%' : '0%', bgcolor: '#9B8CFA', height: '100%', transition: 'width 0.8s ease' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Sparkline data={projectTrend} color="#9B8CFA" />
                  </Box>
                </CardContent>
              </StyledCard>
            </Zoom>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper', overflow: 'visible' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Zap size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                      Quick Actions
                    </Typography>
                  </Box>
                  <Chip
                    icon={<Plus size={14} />}
                    label="Add New"
                    size="small"
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    startIcon={<KeyRound size={18} />}
                    onClick={() => handleOpenDialog("license")}
                    sx={{ flex: "1 1 200px", py: 1.5 }}
                  >
                    Add License
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Server size={18} />}
                    onClick={handleAddSystem}
                    sx={{ flex: "1 1 200px", py: 1.5 }}
                  >
                    Add System
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CameraIconL size={18} />}
                    onClick={() => handleOpenDialog("camera")}
                    sx={{ flex: "1 1 200px", py: 1.5 }}
                  >
                    Add Camera
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={7}>
            <Slide direction="right" in={true} mountOnEnter unmountOnExit>
              <Card sx={{ height: '100%', bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={20} color={theme.palette.primary.main} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                        Active Projects
                      </Typography>
                    </Box>
                    <TextField
                      variant="outlined"
                      placeholder="Search projects..."
                      size="small"
                      value={searchTerm}
                      onChange={handleSearch}
                      sx={{ width: { xs: '100%', sm: 250 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={18} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', bgcolor: 'transparent' }}>
                    <Table sx={{ minWidth: 460 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Project ID</TableCell>
                          <TableCell>Systems</TableCell>
                          <TableCell>Cameras</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projects
                          .filter((project) =>
                            project.project_id &&
                            project.project_id.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .slice(0, 5)
                          .map((project) => {
                            const projectCameras = cameras.filter(camera =>
                              systems.some(s => s.uuid === camera.uuid && s.project_id === project.project_id)
                            ).length;

                            return (
                              <TableRow
                                key={project.project_id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                    {project.project_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={project.systems.length}
                                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={projectCameras}
                                    sx={{ bgcolor: alpha('#16A987', 0.1), color: '#16A987', fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {isMobile ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleProjectSelect(project.project_id)}
                                      sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                                      }}
                                    >
                                      <ChevronRight fontSize="small" />
                                    </IconButton>
                                  ) : (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      endIcon={<ChevronRight />}
                                      onClick={() => handleProjectSelect(project.project_id)}
                                      sx={{ borderRadius: 8 }}
                                    >
                                      View Details
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {projects.length > 5 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        color="primary"
                        onClick={() => setActiveView('projects')}
                      >
                        View All Projects
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Slide>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Slide direction="left" in={true} mountOnEnter unmountOnExit>
              <Card sx={{ height: '100%', bgcolor: darkMode ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.warning.main, 0.02) }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
                      <BellIcon size={20} color={theme.palette.warning.main} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                      Upcoming Expirations
                    </Typography>
                  </Box>
                  <List sx={{ p: 0 }}>
                    {licenses
                      .filter((license) => {
                        const daysToExpire = Math.floor(
                          (new Date(license.end_date) - new Date()) / (1000 * 60 * 60 * 24)
                        );
                        return daysToExpire <= 30 && daysToExpire >= 0;
                      })
                      .slice(0, 5)
                      .map((license, index) => {
                        const daysToExpire = Math.floor(
                          (new Date(license.end_date) - new Date()) / (1000 * 60 * 60 * 24)
                        );
                        const systemDetails = getSystemDetails(systems, license.uuid);

                        return (
                          <Grow in={true} style={{ transitionDelay: `${index * 100}ms` }} key={license.license_id}>
                            <ListItem
                              sx={{
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                mb: 1,
                                bgcolor: darkMode ? '#0E1626' : 'background.paper',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, darkMode ? 0.1 : 0.02) },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                      {license.license_id}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${daysToExpire} days`}
                                      sx={{
                                        bgcolor: daysToExpire <= 7 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                                        color: daysToExpire <= 7 ? 'error.main' : 'warning.main',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                    {systemDetails?.project_id || 'N/A'} • {systemDetails?.software_id || 'N/A'}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          </Grow>
                        );
                      })}
                  </List>
                  {licenses.filter(l => {
                    const days = Math.floor((new Date(l.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                    return days <= 30 && days >= 0;
                  }).length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircle2 size={40} color={theme.palette.success.main} style={{ marginBottom: 16, opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                          No upcoming expirations
                        </Typography>
                      </Box>
                    )}
                </CardContent>
              </Card>
            </Slide>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
}

export default DashboardHome;
